// src/pages/ia-paca/metiers/agentic-ai-engineer.tsx
//
// Page métier — Agentic AI Engineer en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Bot, Workflow, GitBranch, ShieldCheck, Activity, Cpu } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'agentic-ai-engineer',
  metaTitle: 'Recrutement Agentic AI Engineer en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Agentic AI Engineer en PACA : agents autonomes, tool calling, multi-agents, LangGraph, AutoGen, Crew AI, MCP. Le métier émergent qui prendra le dessus en 2026-2027. Salaire 62-130k€.",
  metaKeywords:
    'recrutement Agentic AI Engineer, Agentic AI Marseille, Agents autonomes Sophia, salaire Agentic Engineer 2026, recruter agents IA PACA, LangGraph, MCP',
  heroLabel: 'Agentic AI Engineer',
  heroSubtitle:
    "Le métier qui n'existait pas en 2024 et qui va dominer 2026-2027. Quelqu'un qui construit des agents IA autonomes capables de raisonner, planifier, appeler des outils, collaborer avec d'autres agents — et qui ne plantent pas en prod.",
  heroBadges: ['LangGraph', 'AutoGen', 'CrewAI', 'MCP', 'Multi-agents'],
  jsonLdName: 'Agentic AI Engineer',
  jsonLdDescription:
    "L'Agentic AI Engineer conçoit et industrialise des agents IA autonomes : planification, tool calling, multi-step reasoning, multi-agents, observabilité, garde-fous.",
  jsonLdSkills:
    'LangGraph, AutoGen, CrewAI, LlamaIndex Agents, MCP (Model Context Protocol), tool calling (function calling), planning, ReAct, multi-agent orchestration, eval agents (AgentBench), observability (Langfuse, Phoenix), guardrails, memory / state management',
  jsonLdMinSalary: 62000,
  jsonLdMedianSalary: 90000,
  jsonLdMaxSalary: 130000,
  jsonLdEducation: 'Profil senior tech IA (AI Engineer + LLM expertise).',
  jsonLdExperience: '3-10+ ans, émergent (le rôle n\'existe que depuis 18 mois).',
  intro: {
    title: "Agentic AI Engineer : le métier qui ne reste pas à la mode parce qu'il est réel.",
    paragraphs: [
      "En 2024, les 'agents IA' étaient un sujet Twitter. En 2026, c'est devenu un métier réel avec des cas d'usage en production : agents de support client multi-step, agents commerciaux, agents de recherche, agents de coding (Devin, Cursor Composer, Claude Code). L'Agentic AI Engineer <strong>conçoit, industrialise et opère ces agents</strong>.",
      "Concrètement : il modélise le graphe d'état d'un agent (LangGraph, AutoGen), structure les tools que l'agent peut appeler (function calling, MCP), gère la mémoire court-terme et long-terme, met en place l'observabilité (chaque step trace dans Langfuse), définit des garde-fous (max steps, validation human-in-the-loop, kill switch), et évalue le système globalement (pas juste le LLM, mais le système agent).",
      "<strong>Pourquoi c'est rare et cher :</strong> exige une maturité AI Engineer + une compréhension fine du raisonnement des LLM + une discipline d'observabilité industrielle. Très peu de profils ont fait passer un agent multi-step en production avec un taux de succès >85%. Salaires montent vite (90-130 k€ confirmé en PACA).",
    ],
  },
  stackTitle: 'Les outils que votre Agentic AI Engineer doit maîtriser.',
  stack: [
    {
      Icon: Bot,
      title: 'Frameworks agents',
      tools: ['LangGraph', 'AutoGen', 'CrewAI', 'LlamaIndex Agents', 'Mastra', 'BAML'],
    },
    {
      Icon: Workflow,
      title: 'Orchestration & state',
      tools: ['LangGraph state machines', 'XState (visualisation)', 'Redis pour memory', 'PostgreSQL pour state', 'Event sourcing'],
    },
    {
      Icon: GitBranch,
      title: 'Tools & protocols',
      tools: ['Function calling (OpenAI / Anthropic)', 'MCP (Model Context Protocol)', 'OpenAPI specs', 'Custom Python tools', 'Web automation (Browserbase, Playwright)'],
    },
    {
      Icon: Activity,
      title: 'Observability & eval',
      tools: ['Langfuse', 'Phoenix (Arize)', 'OpenLLMetry', 'AgentBench', 'eval custom multi-step'],
    },
    {
      Icon: ShieldCheck,
      title: 'Garde-fous',
      tools: ['Max iterations', 'Human-in-the-loop', 'Confidence thresholds', 'Tool whitelisting', 'Cost guardrails'],
    },
    {
      Icon: Cpu,
      title: 'Modèles agents',
      tools: ['Claude Sonnet (tool calling fort)', 'GPT-4o', 'o1 / o3 (reasoning)', 'Gemini 2', 'Mistral Large 2'],
    },
  ],
  salaryByCityTitle: '62-130 k€, le rôle qui monte le plus vite en 2026.',
  salaryByCity: [
    { ville: 'Marseille', junior: '62 – 72 k€', confirme: '72 – 100 k€', senior: '100 – 125 k€', expert: '120 – 135 k€' },
    { ville: 'Aix-en-Provence', junior: '60 – 70 k€', confirme: '70 – 97 k€', senior: '97 – 122 k€', expert: '118 – 132 k€' },
    { ville: 'Sophia Antipolis', junior: '65 – 75 k€', confirme: '75 – 105 k€', senior: '105 – 130 k€', expert: '125 – 145 k€' },
    { ville: 'Nice', junior: '62 – 72 k€', confirme: '72 – 100 k€', senior: '100 – 125 k€', expert: '120 – 135 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Agentic AI Engineer.',
  questionsIntro:
    "Ces questions séparent un Agentic AI Engineer qui a mis un agent en prod d'un AI Engineer qui a expérimenté LangGraph en hackathon.",
  questions: [
    {
      q: 'Décris un agent multi-step que tu as mis en production. Quel taux de succès, comment tu mesurais ?',
      good:
        "Cite un cas concret : objectif (ex. agent de réponse client multi-step), taille du graphe d'état, tools disponibles, dataset d'éval avec scénarios représentatifs, métriques (success rate, hallucination rate, coût moyen, latence, fallback humain), itérations pour passer de 60% à 85% de succès.",
      bad: "'On a fait un POC avec LangGraph, ça marchait pas mal.' Pas de métriques, pas de prod, pas d'éval rigoureuse.",
    },
    {
      q: 'Comment tu structures la mémoire (short-term vs long-term) d\'un agent ?',
      good:
        "Short-term = state du graphe en cours (LangGraph state, message history compressé). Long-term = vector DB ou DB structurée selon le besoin (préférences user, historique interactions, faits appris). Mécanisme de retrieval ciblé (pas tout balancer dans le contexte). Stratégie de summarization pour éviter le context overflow.",
      bad: "'On garde tout dans le prompt.' Context blow-up, coûts qui explosent, perte de qualité.",
    },
    {
      q: 'Tu donnes à un agent l\'accès à 30 outils. Comment tu évites qu\'il se perde ?',
      good:
        "Stratégies : 1) sélection dynamique d'outils selon le step (tool routing), 2) hiérarchie d'agents spécialisés (un agent orchestrateur + agents experts), 3) prompts système clairs sur quand utiliser quel outil, 4) testing systématique du tool selection sur dataset d'éval. Cite l'approche \"capability list compression\".",
      bad: "'On les liste tous dans le prompt, le modèle se débrouille.' Confusion garantie au-delà de 7-10 outils.",
    },
    {
      q: 'Comment tu débugges un agent qui boucle ou qui fait n\'importe quoi ?',
      good:
        "Outillage : trace complète via Langfuse / Phoenix (chaque step + tool call + output), replay step-by-step, comparaison vs run de référence. Identifie le step problématique, isole (prompt unitaire), corrige, ré-éval globalement. Connaît les bugs typiques (loop sur tool, hallucination de tool arg, perte de contexte).",
      bad: "'On regarde le log final.' Sans tracing fin, impossible de débugger un agent multi-step.",
    },
    {
      q: "Qu'est-ce que MCP (Model Context Protocol) et pourquoi c'est important pour les agents ?",
      good:
        "Protocol ouvert (Anthropic, fin 2024) qui standardise la façon dont les agents découvrent et appellent des tools externes. Pense USB-C pour les outils LLM. Permet : interopérabilité (les serveurs MCP marchent avec n'importe quel client), écosystème grandissant (FS, GitHub, Slack, etc.), séparation propre client / serveur. Sait l'utiliser et le déployer.",
      bad: "Ne connaît pas le concept. Significatif d'une veille tech faible en 2026.",
    },
  ],
  ctaTitle: 'Vous cherchez un Agentic AI Engineer en PACA ?',
  ctaSubtitle:
    "Profil émergent, très chassé. Notre court-list cible 3-5 candidats avec expérience prod en 25-35 jours.",
};

export default function AgenticAiEngineerPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
